import { ReactNode } from "react";

/** Skeleton placeholder matching a section's approximate height to prevent CLS.
 *  Kept exported for backward compatibility with any external imports. */
export const SectionSkeleton = ({ height = "400px" }: { height?: string }) => (
  <div
    className="w-full bg-muted/30 animate-pulse rounded-sm"
    style={{ minHeight: height }}
    aria-hidden="true"
  />
);

/**
 * Defers RENDERING (not mounting) of off-screen sections using CSS
 * `content-visibility: auto`. Children are mounted in the DOM immediately,
 * so their final layout is committed on first paint — eliminating the
 * skeleton-swap CLS that the previous IntersectionObserver approach caused.
 *
 * `contain-intrinsic-size` reserves vertical space until the browser
 * actually paints the section, preserving scroll position and preventing
 * layout shift in surrounding content.
 *
 * Behavior preserved: rendering work for sections far from the viewport
 * is still skipped by the browser, so initial JS/paint cost stays low.
 */
export const LazySection = ({
  children,
  fallbackHeight = "400px",
}: {
  children: ReactNode;
  fallbackHeight?: string;
  /** Accepted for API compatibility; no longer used. */
  rootMargin?: string;
}) => {
  return (
    <div
      style={{
        contentVisibility: "auto",
        containIntrinsicSize: `auto ${fallbackHeight}`,
      }}
    >
      {children}
    </div>
  );
};
